// src/features/WasherAnalysis/WasherDetailPage.js
import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Grid,
  useTheme,
  alpha,
  Stack,
  Divider,
  CircularProgress,
  Alert // ДОДАВ ЦЕЙ ІМПОРТ
} from '@mui/material';
import { 
  ArrowBack, 
  TrendingUp, 
  ShowChart, 
  CalendarToday,
  Speed,
  LocalLaundryService
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useI18n } from '../../i18n/i18n';
import { useWasherData, WASHER_PROGRAMS } from './hooks/useWasherData';

const StatCard = ({ title, value, unit, subtitle, color = "primary", icon: Icon }) => {
  const theme = useTheme();
  
  return (
    <Card 
      sx={{ 
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.1)} 0%, ${alpha(theme.palette[color].light, 0.05)} 100%)`,
        border: `1px solid ${alpha(theme.palette[color].main, 0.2)}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px ${alpha(theme.palette[color].main, 0.15)}`,
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: '12px',
              background: `linear-gradient(135deg, ${theme.palette[color].main} 0%, ${theme.palette[color].dark} 100%)`,
              boxShadow: `0 4px 12px ${alpha(theme.palette[color].main, 0.3)}`
            }}
          >
            <Icon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette[color].dark }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
        
        <Typography variant="h3" sx={{ fontWeight: 800, color: theme.palette[color].dark, mb: 1 }}>
          {value}
          {unit && (
            <Typography component="span" variant="h5" sx={{ fontWeight: 600, color: 'text.secondary', ml: 0.5 }}>
              {unit}
            </Typography>
          )}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default function WasherDetailPage() {
  const { t } = useI18n();
  const { programKey } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const { selectedProgramData, loading, error } = useWasherData(programKey);

  const programConfig = WASHER_PROGRAMS[programKey];
  const isRejection = programConfig?.type === 'rejection';
  const isWeight = programConfig?.type === 'weight';

  const getColor = () => {
    if (isRejection) return 'error';
    if (isWeight) return 'success';
    return 'primary';
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        flexDirection: 'column',
        gap: 3
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          {t('common_loading')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/washer-analysis')}>
          {t('common_back')}
        </Button>
      </Box>
    );
  }

  if (!programConfig || !selectedProgramData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6" sx={{ mb: 2 }}>
          Програма не знайдена: {programKey}
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Доступні програми: {Object.keys(WASHER_PROGRAMS).join(', ')}
        </Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/washer-analysis')}>
          {t('common_back')}
        </Button>
      </Box>
    );
  }

  const { data, statistics } = selectedProgramData;

  return (
    <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
      {/* Заголовок та навігація */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={() => navigate('/washer-analysis')}
          variant="outlined"
          sx={{ borderRadius: '8px' }}
        >
          {t('common_back')}
        </Button>
        
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 60,
            height: 60,
            borderRadius: '16px',
            background: `linear-gradient(135deg, ${theme.palette[getColor()].main} 0%, ${theme.palette[getColor()].dark} 100%)`,
            boxShadow: `0 8px 24px ${alpha(theme.palette[getColor()].main, 0.3)}`
          }}
        >
          <LocalLaundryService sx={{ color: 'white', fontSize: 32 }} />
        </Box>
        
        <Box sx={{ flex: 1 }}>
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
            {programConfig.label}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip 
              label={programConfig.unit} 
              color={getColor()}
              sx={{ fontWeight: 600 }}
            />
            <Chip 
              label={isRejection ? t('washer_type_rejection') : isWeight ? t('washer_type_weight') : t('washer_type_cycles')} 
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          </Stack>
        </Box>
      </Stack>

      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('washer_stats_total')}
            value={statistics.total.toLocaleString('es-ES')}
            unit={programConfig.unit}
            subtitle={t('washer_stats_total_desc')}
            color={getColor()}
            icon={TrendingUp}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('washer_stats_avg')}
            value={statistics.average.toFixed(1)}
            unit={programConfig.unit}
            subtitle={t('washer_stats_avg_desc')}
            color="info"
            icon={ShowChart}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('washer_stats_max')}
            value={statistics.max.toLocaleString('es-ES')}
            unit={programConfig.unit}
            subtitle={t('washer_stats_max_desc')}
            color="warning"
            icon={Speed}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('washer_stats_days')}
            value={statistics.daysWithData}
            unit={t('washer_stats_days_unit')}
            subtitle={t('washer_stats_days_desc')}
            color="success"
            icon={CalendarToday}
          />
        </Grid>
      </Grid>

      {/* Детальна таблиця */}
      <Card 
        sx={{ 
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.7)} 100%)`,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)'
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, pb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {t('washer_detail_full_history')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('washer_analysis_chronology')}
            </Typography>
          </Box>
          
          <Divider />
          
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="medium">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', py: 2 }}>
                    {t('washer_analysis_date')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', py: 2 }}>
                    {t('washer_analysis_shift')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.9rem', py: 2 }}>
                    {programConfig.unit}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', py: 2 }}>
                    {t('washer_analysis_operators')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length > 0 ? (
                  data.map((record, index) => (
                    <TableRow 
                      key={index} 
                      hover
                      sx={{ 
                        '&:last-child td': { borderBottom: 0 },
                        transition: 'background-color 0.2s ease'
                      }}
                    >
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {new Date(record.date).toLocaleDateString('es-ES')}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Chip 
                          label={record.shift} 
                          size="small" 
                          variant="outlined" 
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ py: 2 }}>
                        <Chip 
                          label={record.value.toLocaleString('es-ES')}
                          color={getColor()}
                          variant="filled"
                          sx={{ fontWeight: 600, minWidth: 80 }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2">
                          {record.operators?.join(', ') || 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      <Typography variant="body1">
                        {t('washer_analysis_no_data')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}


